import { Dispatch, SetStateAction } from "react";
import {
  FieldValues,
  UseFormGetValues,
  UseFormSetValue,
} from "react-hook-form";
import { getLPFilterRequest, LPFRProps } from "./filter-obj";
import {
  getListadoPersonas,
  GetListadoPersonasProps,
  GetListadoPersonasResponse,
  AssociateAccountToPersonProps,
  CreatePersonProps,
  createPerson,
  getPersonaDetalle,
  associateAccountToPerson,
  unlinkAccounts,
  UnlinkAccountsProps,
} from "@/services/persons.service";
import { AxiosError } from "axios";
import { handlePermissionError } from "@/utils/permission-check";
import { NavigateFunction } from "react-router-dom";
import { toaster } from "@/components/ui/toaster";
import { getAxiosErrorMessage } from "@/utils/axios-error-message";
import { AppRoutes } from "@/constants/routes";
import { CuentaRelacionada, PersonaDetalle } from "./person.schema";

interface ClearFiltersHandlerProps {
  setValue: UseFormSetValue<FieldValues>;
  submitFilters: (event: FieldValues) => Promise<void>;
  getValues: UseFormGetValues<FieldValues>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
}

export const clearFiltersHandler = ({
  getValues,
  setValue,
  submitFilters,
  setFiltersApplied,
}: ClearFiltersHandlerProps) => {
  setValue("first_name", "");
  setValue("last_name", "");
  setValue("cuit", "");
  setValue("dni", "");
  setFiltersApplied(false);

  submitFilters?.(getValues());
};

interface SubmitFiltersHandlerProps {
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  setPage: Dispatch<SetStateAction<number>>;
  setCurrentFilters: Dispatch<SetStateAction<{}>>;
  fetchPersons: (filters?: any) => Promise<void>;
  event: FieldValues;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const submitFiltersHandler = async ({
  event,
  fetchPersons,
  setCurrentFilters,
  setFiltersApplied,
  setOpenFilters,
  setPage,
  navigate,
  fetchPermissions,
}: SubmitFiltersHandlerProps) => {
  try {
    setPage(1);
    setFiltersApplied(true);

    const filterObj: Partial<GetListadoPersonasProps> = getLPFilterRequest(
      event as LPFRProps,
    );

    setCurrentFilters(filterObj);

    if (!Object.keys(filterObj)?.length) setFiltersApplied(false);
    await fetchPersons(filterObj);

    setOpenFilters(false);
  } catch (error) {
    const errorObj = error as AxiosError<{
      message: string;
      tiposFaltantes?: string[];
    }>;
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title:
          errorObj?.response?.data?.message ??
          "No se pudieron aplicar los filtros.",
      });
    }
    throw error;
  }
};

interface CreatePersonHandlerProps {
  getValues: () => any;
  setLoadingCreatePerson: Dispatch<SetStateAction<boolean>>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const createPersonHandler = async ({
  getValues,
  setLoadingCreatePerson,
  navigate,
  fetchPermissions,
}: CreatePersonHandlerProps) => {
  setLoadingCreatePerson(true);
  try {
    const values = getValues();

    const objToSend: CreatePersonProps = {
      agro: values?.agro?.[0],
      apellido: values?.apellido,
      nombre: values?.nombre,
      apodo: values?.apodo,
      cuenta: values?.cuenta?.value,
      cuit: values?.cuit,
      dni: values?.dni,
      sexoBiologico: values?.sexoBiologico?.value,
      fecha: values?.fecha,
      hobbies: values?.hobbies?.map((el: any) => Number(el.value)) ?? [],
      idEstadoCivil: values?.estadoCivil?.value,
      idNivelEstudio: values?.nivelEstudio?.value,
      idProfesion: values?.profesion?.value,
      localidad: values?.localidad,
      mail: values?.mail,
      telefono: values?.telefono,
    };

    await createPerson(objToSend);

    toaster.create({
      type: "success",
      title: "Persona creada con éxito",
    });

    navigate(AppRoutes.PERSONS);
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.PERSONS,
    );
    if (!handled) {
      const errorMessage = getAxiosErrorMessage(
        error,
        "No se pudo crear la persona",
      );
      toaster.create({
        type: "error",
        title: errorMessage,
      });
    }
    throw error;
  } finally {
    setLoadingCreatePerson(false);
  }
};

interface FetchPersonDetailHandlerProps {
  id: number;
  setLoadingPersonData: Dispatch<SetStateAction<boolean>>;
  setPersonData: Dispatch<SetStateAction<PersonaDetalle | null | undefined>>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  routeOnPermissionError: string;
}

export const fetchPersonDetailHandler = async ({
  id,
  setLoadingPersonData,
  setPersonData,
  navigate,
  fetchPermissions,
  routeOnPermissionError,
}: FetchPersonDetailHandlerProps) => {
  setLoadingPersonData(true);
  try {
    const response = await getPersonaDetalle({ id });
    setPersonData(response);
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      routeOnPermissionError,
    );
    if (!handled) {
      const errorMessage = getAxiosErrorMessage(
        error,
        "No se pudieron obtener los datos de la persona",
      );
      toaster.create({
        type: "error",
        title: errorMessage,
      });
    }
    throw error;
  } finally {
    setLoadingPersonData(false);
  }
};

interface AssociateAccountHandlerProps {
  event: FieldValues;
  personId: number;
  setLoadingAssociateAccount: Dispatch<SetStateAction<boolean>>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const associateAccountHandler = async ({
  event,
  personId,
  setLoadingAssociateAccount,
  navigate,
  fetchPermissions,
}: AssociateAccountHandlerProps) => {
  setLoadingAssociateAccount(true);
  try {
    const objToSend: AssociateAccountToPersonProps = {
      idPersona: Number(personId),
      cuenta: event?.cuenta?.value,
    };

    await associateAccountToPerson(objToSend);

    toaster.create({
      type: "success",
      title: "Cuenta asociada con éxito.",
    });

    navigate(AppRoutes.PERSON_DETAIL.replace(":id", String(personId)), {
      state: {
        tab: "cuentas_asociadas",
      },
    });
  } catch (error) {
    const route = AppRoutes.PERSON_DETAIL.replace(":id", String(personId));
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      route,
      {
        tab: "cuentas_asociadas",
      },
    );
    if (!handled) {
      const errorMessage = getAxiosErrorMessage(
        error,
        "No se pudo asociar la cuenta.",
      );
      toaster.create({
        type: "error",
        title: errorMessage,
      });
    }
    throw error;
  } finally {
    setLoadingAssociateAccount(false);
  }
};

export const handleItemCheckChangeHandler = (
  prevSelection: number[],
  changes: { checked: boolean | string },
  item: CuentaRelacionada,
) => {
  const isChecked = Boolean(changes.checked);
  return isChecked
    ? [...prevSelection, item?.cuenta]
    : prevSelection.filter((id) => id !== item?.cuenta);
};

interface UnlinkAccountsHandlerProps {
  personId: number;
  selection: number[];
  setSelection: Dispatch<SetStateAction<number[]>>;
  setLoadingUnlinkAccounts: Dispatch<SetStateAction<boolean>>;
  fetchPersonDetail: (id: number) => Promise<void>;
  setOpenUnlink: (open: boolean) => void;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const unlinkAccountsHandler = async ({
  personId,
  selection,
  setSelection,
  setLoadingUnlinkAccounts,
  fetchPersonDetail,
  setOpenUnlink,
  navigate,
  fetchPermissions,
}: UnlinkAccountsHandlerProps) => {
  setLoadingUnlinkAccounts(true);
  try {
    const objToSend: UnlinkAccountsProps = {
      idPersona: Number(personId),
      cuentas: selection?.map(String),
    };

    await unlinkAccounts(objToSend);

    setSelection([]);
    toaster.create({
      type: "success",
      title: "Cuentas seleccionadas desasociadas con éxito.",
    });

    await fetchPersonDetail(Number(personId));
    setOpenUnlink(false);
  } catch (error) {
    const route = AppRoutes.PERSON_DETAIL.replace(":id", String(personId));
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      route,
      {
        tab: "cuentas_asociadas",
      },
    );
    if (!handled) {
      const errorMessage = getAxiosErrorMessage(
        error,
        "No se pudo desasociar las cuentas seleccionadas.",
      );
      toaster.create({
        type: "error",
        title: errorMessage,
      });
    }
  } finally {
    setLoadingUnlinkAccounts(false);
    setOpenUnlink(false);
  }
};

interface FetchPersonsHandlerProps {
  setLoadingPersons: Dispatch<SetStateAction<boolean>>;
  setPersons: Dispatch<SetStateAction<GetListadoPersonasResponse | null>>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  showLoad?: boolean;
  filters?: GetListadoPersonasProps;
}

export const fetchPersonsHandler = async ({
  setLoadingPersons,
  setPersons,
  navigate,
  fetchPermissions,
  showLoad = true,
  filters,
}: FetchPersonsHandlerProps) => {
  try {
    if (showLoad) setLoadingPersons(true);

    const response = await getListadoPersonas({
      ...filters,
      "P.Pagina": filters?.["P.Pagina"] ?? 1,
      "P.Records": 10,
    });

    setPersons(response);

    setLoadingPersons(false);
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title: "No se pudo cargar las personas.",
      });
    }
  } finally {
    setLoadingPersons(false);
  }
};
